import React, { useState, ReactElement } from 'react';

interface CustomExpanderProps {
  children: [ReactElement, ReactElement];
  defaultExpanded?: boolean;
  badge?: number;
}

export const CustomExpander: React.FC<CustomExpanderProps> = ({
  children,
  defaultExpanded = false,
  badge,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);

  // Destructure children into header and content
  const [headerContent, expandableContent] = React.Children.toArray(
    children,
  ) as [ReactElement, ReactElement];

  const styles = {
    container: {
      width: '100%',
      backgroundColor: 'transparent',
      marginBottom: '4px',
      borderRadius: '8px',
      border: '1px solid transparent',
      transition: 'all 0.2s ease',
    },
    containerExpanded: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      cursor: 'pointer',
      userSelect: 'none' as const,
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      backgroundColor: 'transparent',
      gap: '12px',
      direction: 'rtl' as const,
    },
    headerHover: {
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
    },
    chevron: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      transition: 'all 0.2s ease',
      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
      color: isExpanded ? '#1976d2' : '#666',
    },
    titleContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    title: {
      fontSize: '1rem',
      fontWeight: isExpanded ? 600 : 500,
      color: isExpanded ? '#1976d2' : '#333',
      margin: 0,
      transition: 'all 0.2s ease',
    },
    badge: {
      backgroundColor: isExpanded ? '#1976d2' : '#666',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '20px',
      transition: 'all 0.2s ease',
    },
    content: {
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      backgroundColor: 'transparent',
      padding: isExpanded ? '8px 16px 16px' : '0 16px',
      maxHeight: isExpanded ? '1000px' : '0',
      opacity: isExpanded ? 1 : 0,
      transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
    },
  };

  return (
    <div
      style={{
        ...styles.container,
        ...(isExpanded ? styles.containerExpanded : {}),
      }}
    >
      <div
        style={{
          ...styles.header,
          ...(isHovered ? styles.headerHover : {}),
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.chevron}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div style={styles.titleContainer}>
          <div style={styles.title}>{headerContent}</div>
          {badge !== undefined && badge > 0 && (
            <div style={styles.badge}>{badge}</div>
          )}
        </div>
      </div>
      <div style={styles.content}>{expandableContent}</div>
    </div>
  );
};
